import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import {
    TagState,
    TagType,
    useGetTagProductsQuery,
    useGetTagQuery,
    useUpdateTagMutation,
    TotadminPermission,
} from 'data/__generated__';
import { PAGES } from '../../constants/pages';

import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import { SectionContainer } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { Loader, LoaderModeType } from 'components/Loader';
import { TagForm } from './components/TagForm';
import { useHasPermission } from 'hooks/useHasPermission';

type FormType = {
    _id: string;
    displayIndex: number;
    name: string;
    type: TagType | null;
    state: TagState | null;
};

type ParamTypes = {
    tagId: string;
};

export const TagUpdate = () => {
    const { tagId = '' } = useParams<ParamTypes>();

    const { loading: tagLoading, data: tagData, error: tagError } = useGetTagQuery({ variables: { tagId } });

    // Les produits associés sont une autre ressource : sans le droit, la section est verrouillée et la requête ne part pas.
    const canReadProducts = useHasPermission(TotadminPermission.ProductsRead);
    const { loading: productsLoading, data: productsData } = useGetTagProductsQuery({
        variables: { tagId },
        skip: !canReadProducts,
    });

    const [updateTag] = useUpdateTagMutation();
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, formData: FormType) => {
        e.preventDefault();
        const { displayIndex, name, type, state, _id } = formData;
        if (!type) {
            toast.error('Il manque le champ type');
            return;
        }

        if (!state) {
            toast.error('Il manque le champ state');
            return;
        }

        if (!displayIndex || !Number.isInteger(Number(displayIndex))) {
            toast.error("Index d'affichage devrait être un nombre entier");
            return;
        }

        if (state === TagState.Archived && productsData?.productsWithTagId.length) {
            toast.error('Un tag ne devrait avoir aucun produit associé pour être archivé');
            return;
        }

        const result = await updateTag({
            variables: {
                tag: {
                    _id,
                    displayIndex: Number(displayIndex),
                    name,
                    type,
                    state,
                },
            },
        });
        const { data } = result;
        if (data) {
            const {
                updateTag: { success },
            } = data;
            if (success) {
                toast.success(`Le tag "${formData.name}" a bien été mis à jour !`);
            } else {
                const {
                    updateTag: { error },
                } = data;
                if (error) {
                    toast.error(`L'erreur suivante est survenue : ${error}`);
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    };

    function canISubmit(submitData: FormType): boolean {
        return submitData.name !== '';
    }

    if (tagLoading || tagError || !tagData) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    const {
        tag: { _id, displayIndex, name, type, state },
    } = tagData;

    const initialFormData = {
        _id,
        displayIndex,
        name,
        type,
        state,
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.tagUpdate} />
                </HeaderTitle>
                <Link to="/tags">
                    <TotemPrimaryButton $isSecondaryStyle>Retour</TotemPrimaryButton>
                </Link>
            </Header>
            <Content>
                <ScrollableContent>
                    <SectionContainer title="Informations générales" isInitiallyOpen>
                        <TagForm<FormType>
                            handleSubmit={handleSubmit}
                            initialFormData={initialFormData}
                            canISubmit={canISubmit}
                            submitMessage="Mettre à jour le tag"
                        />
                    </SectionContainer>
                    <SectionContainer
                        title="Produits associés"
                        isInitiallyOpen
                        requiredPermissions={[TotadminPermission.ProductsRead]}
                    >
                        <ProductsContainer>
                            {productsLoading ? (
                                <Loader mode={LoaderModeType.Spin} />
                            ) : productsData?.productsWithTagId.length ? (
                                productsData.productsWithTagId.map((product) => (
                                    <StyledLink key={product._id} to={`/product/${product._id}`}>
                                        • {product.fullname}
                                    </StyledLink>
                                ))
                            ) : (
                                'Aucun produit associé'
                            )}
                        </ProductsContainer>
                    </SectionContainer>
                    <AuditLogsSection collectionDocumentIds={[tagId]} />
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const ProductsContainer = styled.div`
    display: flex;
    flex-direction: column;
    /* La liste peut être longue : elle défile dans sa carte plutôt que d'étirer la page. */
    max-height: 320px;
    overflow-y: auto;
`;

const StyledLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryHoveredColor};
    text-decoration: none;
    font-size: 16px;
    text-overflow: ellipsis;
    &:hover {
        text-decoration: underline;
    }
`;
